import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useLayoutEffect,
} from "react";
import {
  RetorikAgent,
  AvailableViews,
  sendEvent,
  setPrintingCallback,
  useKioskParameterOpening,
  resetStores,
  addDetailedAttachmentTrigger,
  setLoaderClosed,
  useMode,
  sendMessage,
  sendActivity,
} from "@davi-ai/retorik-framework";
import type {
  Configuration,
  ViewsConfiguration,
  ChatbotData,
  AddressData,
  UserData,
} from "@davi-ai/retorik-framework";
import { FullMenu } from "@davi-ai/retorik-framework-hospitality";
import { Map, MapIcon, labels } from "@davi-ai/retorik-map";
import type { Tenant } from "@davi-ai/retorik-map";
import Detail_POI from "../../components_cci/Detail_POI"
import useConfiguration from "../../redux/Hooks/Kiosk/useConfiguration";
import { useAgent, useChatbot, useViewsConfiguration } from "../../redux/Hooks";
import { useDispatchKiosk } from "../../redux";
import { configurationChangeAction } from "../../redux/Actions";
import settingsOpenedToggleAction from "../../redux/Actions/Kiosk/settingsOpenedToggleAction";
import useVoice from "../../redux/Hooks/useVoice";
import useDefaultMode from "../../redux/Hooks/Kiosk/useDefaultMode";
import usePrinter from "../../redux/Hooks/Kiosk/usePrinter";
import useParameterOpening from "../../redux/Hooks/Auth/useParameterOpening";

import type { Agent } from "../../model/Agent";

import { NoiseSuppressorWorklet_Name } from "@timephy/rnnoise-wasm";

import LoginWindow from "../Settings/LoginWindow";
import Settings from "../SettingsScreen";
import Loader from "../Common/Loader";
import { defaultConfig } from "../../data";
import { buildCustomMenu } from "../../utils/customJOMenu";

const actionsForVeilleRefresh = [
  "DIRECT_LINE/POST_ACTIVITY",
  "DIRECT_LINE/INCOMING_ACTIVITY",
  "WEB_CHAT/MARK_ACTIVITY",
];

const allowedPrefixes = ["dev", "staging", "preview", "experimental"];

const userData: UserData = {
  name: "user",
  id: `kiosk_user_${Date.now()}`,
  username: "",
  nom: "",
  prenom: "",
  email: "",
  token: "",
  referrer: "",
  ipaddress: "",
};

const baseMenu = [
  { view: AvailableViews.home, indice: 1 },
  // {
  //   customDisplay: "mapDisplay",
  //   label: labels,
  //   icon: <MapIcon />,
  //   indice: 2,
  // },
  {
    customDisplay: "mapDisplay",
    label: labels,
    icon : React.createElement("img", {
      src: "https://studio.retorik.ai/mejstjegx80had7vep/media/icon/svg/localisation.svg",
      className: "rf-h-full rf-w-full",
      style: { color: "red", fill: "none", stroke: "red" },
    }),
    indice: 2,
  },
  /*{ view: AvailableViews.news, indice: 3 },
  { view: AvailableViews.weather, indice: 4 },
  { view: AvailableViews.emergency, indice: 5 },*/
];

//const customMenu = [new FullMenu(sendEvent).menu];

const Chatbot = (props: { timerRefresh: Function }) => {
  const dispatch = useDispatchKiosk();
  
  const retorikConfiguration: Configuration = useConfiguration();
  const viewsConfiguration: ViewsConfiguration = useViewsConfiguration();
  const chatbotData: ChatbotData | undefined = useChatbot();
  const agent: Agent = useAgent();
  const voice = useVoice();
  const defaultMode = useDefaultMode();
  const printer = usePrinter();
  const openingLimit = useParameterOpening();
  
  const askedForKioskParametersOpening = useKioskParameterOpening();
  
  const [retorikMap, setRetorikMap] = useState<JSX.Element>(<></>);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [loggingIn, setLoggingIn] = useState<boolean>(false);
  
  const printerRef = useRef<string | null>(printer);
  
  const setAudioWorklet = async (): Promise<void> => {
    const audioContext = new AudioContext();
    await audioContext.resume();
    try {
      await audioContext.audioWorklet.addModule("/worklet/NoiseSuppressorWorklet.worklet.js");
      const rnnoiseNode = new AudioWorkletNode(audioContext, NoiseSuppressorWorklet_Name);
      
      // Get current audio stream containing the microphone track, and apply the denoiser
      navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        if (stream) {
          const source = audioContext.createMediaStreamSource(stream);
          source.connect(rnnoiseNode) // pass audio through noise suppression
        }
      })
      .catch((e) => console.warn(e));
    } catch (e:any) {
      // Ignore the error if the module is already added.
      if (e.name !== 'AbortError') {
        console.error("Failed to add worklet module", e);
      }
    }
    
  };
  
  useLayoutEffect(() => {
    //setAudioWorklet();
    resetStores();
    
    return () => {
      resetStores();
    };
  }, []);
  
  useEffect(() => {
    dispatch(settingsOpenedToggleAction(showSettings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSettings]);
  
  /**
  * Wait for a call from Retorik Framework to log in and open parameters
  */
  useEffect(() => {
    if (askedForKioskParametersOpening) {
      if (openingLimit) {
        const now = new Date();
        now < openingLimit ? setShowSettings(true) : setLoggingIn(true);
      } else {
        setLoggingIn(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askedForKioskParametersOpening]);
  
  const addressData: AddressData | null = useMemo(() => {
    if (agent?.tenant) {
      let prefix: string | undefined = undefined;
      if (agent.baseUri) {
        const temp = agent.baseUri.split(".")[0];
        allowedPrefixes.includes(temp) && (prefix = temp);
      }
      
      return {
        tenant: agent.tenant,
        prefix: prefix,
      };
    }
    
    return null;
  }, [agent]);
  
  const customVoice = useMemo(() => voice || {}, [voice]);
  
  // Set logo from agent data if present
  useEffect(() => {
    agent?.agentLogoUri &&
    dispatch(
      configurationChangeAction({
        ...retorikConfiguration,
        logo: { src: agent.agentLogoUri },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent]);
  
  useEffect(() => {
    if (agent?.tenant && retorikConfiguration) {
      const userLoc =
      retorikConfiguration.position?.latitude &&
      retorikConfiguration.position.longitude
      ? {
        lat: retorikConfiguration.position.latitude,
        lng: retorikConfiguration.position.longitude,
      }
      : undefined;
      
      const tenantList: { [key: string]: Tenant } = {};
      tenantList[agent.tenant.replaceAll("asklea_", "")] = {
        name: agent.tenant.replaceAll("asklea_", ""),
        retorikName: agent.tenant,
        areaLocation: agent.agentZone ? agent.agentZone.zone : [],
        aroundMeDistance: 15000,
      };
      
      let prefix: string | undefined = undefined;
      if (agent.baseUri) {
        const temp = agent.baseUri.split(".")[0];
        allowedPrefixes.includes(temp) && (prefix = temp);
      }
      
      setRetorikMap(
        <Map
        activeTenant={agent.tenant.replaceAll("asklea_", "")}
        tenantList={tenantList}
        userLocation={userLoc}
        isDevTenant={prefix === "dev"}
        />
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retorikConfiguration]);
  
  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };
  
  useEffect(() => {
    printerRef.current = printer;
  }, [printer]);
  
  /**
  * Call for silent printing if we are in an electron context, or open the file for preview / printing otherwise
  * @param url string
  */
  const printingCallback = async (url: string): Promise<void> => {
    if (url) {
      
      try {
        fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/pdf",
          },
        })
        .then((data) => {
          return data.blob();
        })
        .then((blob) => {
          const blobURL = URL.createObjectURL(blob);
          if (blobURL) {
            window.open(blobURL);
          }
        })
        .catch((error) => {
          console.warn("Fetch error : ", error);
        });
      } catch (e) {
        console.warn(e);
      }
      
    }
  };
  
  const handleActionType = (action: any) => {
    //console.log("Action : ", action); 
    // Intercept WEB_CHAT/SEND_MESSAGE events coming from the chat widget
    // and transform specific activity.value payloads.
    try {
      if (
        action?.type === "WEB_CHAT/SEND_MESSAGE" &&
        action.payload?.activity?.value &&
        action.payload.activity.value.name === "hospitality.showPoiById"
      ) {
        const oldValue = action.payload.activity.value;
        
        // By default keep the same identifier unless you provide a mapping.
        // If you need to map identifiers, update this map accordingly.
        const idMap: { [key: string]: string } = {
          // "4d62d36xtvz4hsd6cypryqssxp": "47y76v17p3k21r9mk8ymgj89xv",
        };
        
        const newIdentifier = idMap[oldValue.identifier] || oldValue.identifier;
        
        // Replace only the value object as requested
        action = {
          ...action,
          payload: {
            ...action.payload,
            activity: {
              ...action.payload.activity,
              value: {
                name: "hospitality.showContentItemById",
                identifier: newIdentifier,
              },
            },
          },
        };
        
        console.log("Transformed action value:", action.payload.activity.value);
        sendActivity(action.payload.activity);
        return action;  
      }
    } catch (e) {
      console.warn("Error while transforming WEB_CHAT/SEND_MESSAGE action:", e);
    }
    // Refresh veille timer when an action's type is part of the array actionsForVeilleRefresh
    if (action.type && actionsForVeilleRefresh.includes(action.type)) {
      props.timerRefresh();
    }
    // Open settings panel when event kiosk.ShowSettings is called
    if (
      action.payload?.activity?.type === "event" &&
      action.payload.activity.name === "kiosk.ShowSettings"
    ) {
      setShowSettings(true);
    }
    // Set printing callback on directline connection
    if (action.type === "DIRECT_LINE/CONNECT") {
      setPrintingCallback(printingCallback);
    }
    
    if(action.type === "DIRECT_LINE/CONNECT_FULFILLED") {
      addAttachmentTriggers();
      injectCSS();
    }
    
    return false;
  };
  
  const handleSuccessfullValidation = (): void => {
    setShowSettings(true);
    setLoggingIn(false);
  };
  
  const positionCCI = {
    latitude:46.98853302001953,
    longitude:3.1573901176452637,
  }
  
  function isMobile() {
    return window.matchMedia("(max-width: 767px)").matches;
  }
  
  let customCSS = ``;
  
  let customInjectedCSS = `
    div[data-testid="widget-wrapper"] {
      z-index: 9999;
    }
    .filterCategory span{
      color: black !important;
    }
    .filterCategory .image{
      padding:0 !important;
    }
      .poiButton{
        color : black !important;
        border-color : black !important;
      }
    #retorik-container{
      height: 100%;
      width: 100%;
    }
    #retorik-widget-button{
      z-index: 999;
    }
    `;
  
  if (isMobile()) {
    customInjectedCSS = "";
    customCSS = ``;
  }
  
  function injectCSS() {
    console.log("Inject CSS");
    const host = document.querySelector("#retorik-shadow");
    if (
      host &&
      host.shadowRoot &&
      document.getElementById("injectedStyle") == null
    ) {
      // Inject shadow dom custom CSS
      console.log("styleToInject in shadow", customInjectedCSS);
      const injectedStyle = document.createElement("style");
      injectedStyle.id = "injectedStyle";
      injectedStyle.innerHTML = customInjectedCSS;
      if (host && host.shadowRoot) host.shadowRoot.appendChild(injectedStyle);
    }
    
    if (document.getElementById("styleToInject") == null) {
      console.log("styleToInject", customCSS);
      const style = document.createElement("style");
      style.id = "styleToInject";
      style.innerHTML = customCSS;
      document.body.appendChild(style);
    }
  }
  
  
  const externalComponents = [
    {
      name: "mapDisplay",
      component: (
        <Map
        tenantList={{
          CCI: {
            name: "CCI",
            retorikName: "mejstjegx80had7vep",
            areaLocation: [],
            aroundMeDistance: 1500,
          }
        }}
        activeTenant="CCI"
        userLocation={[positionCCI.latitude, positionCCI.longitude]}
        limitAroundCenter={1000}
        //mapCenter={}
        settings={{categories: [
          {
            name: 'FireOrganization',
            filter: {
              iconUrl: 'https://studio.retorik.ai/mejstjegx80had7vep/media/icon/feu_couleur.svg',//png
              //label: ,//single lang
              localizedLabel: { //multi lang
                'fr-FR': 'feu/metal',
                'en-US': 'fire/metal'
              }
            },
            color: 'white'
          },
          {
            name: 'WaterOrganization',
            filter: {
              iconUrl: 'https://studio.retorik.ai/mejstjegx80had7vep/media/icon/eau_couleur.svg ',//png
              //label: ,//single lang
              localizedLabel: { //multi lang
                'fr-FR': 'eau',
                'en-US': 'water'
              }
            },
            color: 'white'
          },
          {
            name: 'WindOrganization',
            filter: {
              iconUrl: 'https://studio.retorik.ai/mejstjegx80had7vep/media/icon/air_couleur.svg',//png
              //label: ,//single lang
              localizedLabel: { //multi lang
                'fr-FR': 'air',
                'en-US': 'wind'
              }
            },
            color: 'white'
          },
          {
            name: 'WoodOrganization',
            filter: {
              iconUrl: 'https://studio.retorik.ai/mejstjegx80had7vep/media/icon/bois_couleur.svg  ',//png
              //label: ,//single lang
              localizedLabel: { //multi lang
                'fr-FR': 'bois',
                'en-US': 'wood'
              }
            },
            color: 'white'
          },
          {
            name: 'DirtOrganization',
            filter: {
              iconUrl: 'https://studio.retorik.ai/mejstjegx80had7vep/media/icon/terre_couleur.svg',//png
              //label: ,//single lang
              localizedLabel: { //multi lang
                'fr-FR': 'terre',
                'en-US': 'dirt'
              }
            },
            color: 'white'
          },
          {
            name: 'OtherOrganization',
            filter: {
              iconUrl: 'https://studio.retorik.ai/mejstjegx80had7vep/media/icon/autre_couleur.svg',//png
              //label: ,//single lang
              localizedLabel: { //multi lang
                'fr-FR': 'autre réussites',
                'en-US': 'other achievements'
              }
            },
            color: 'white'
          }
          
        ]}}
        />
      ),
    },
  ];
  
  const addAttachmentTriggers = () => {
    console.log("Adding attachment triggers for POI details");
    addDetailedAttachmentTrigger({
      type: 'event',
      name: 'hospitality.ShowContentItem',
      contentType: 'FireOrganization',
      // @ts-ignore
      linkedComponent: Detail_POI,
    });
    
    addDetailedAttachmentTrigger({
      type: 'event',
      name: 'hospitality.ShowContentItem',
      contentType: 'WaterOrganization',
      // @ts-ignore
      linkedComponent: Detail_POI,
    });
    
    addDetailedAttachmentTrigger({
      type: 'event',
      name: 'hospitality.ShowContentItem',
      contentType: 'WoodOrganization',
      // @ts-ignore
      linkedComponent: Detail_POI,
    });
    
    addDetailedAttachmentTrigger({
      type: 'event',
      name: 'hospitality.ShowContentItem',
      contentType: 'OtherOrganization',
      // @ts-ignore
      linkedComponent: Detail_POI,
    });
    
    addDetailedAttachmentTrigger({
      type: 'event',
      name: 'hospitality.ShowContentItem',
      contentType: 'WindOrganization',
      // @ts-ignore
      linkedComponent: Detail_POI,
    });
    
    addDetailedAttachmentTrigger({
      type: 'event',
      name: 'hospitality.ShowContentItem',
      contentType: 'DirtOrganization',
      // @ts-ignore
      linkedComponent: Detail_POI,
    });
    
    addDetailedAttachmentTrigger({
      type: 'event',
      name: 'hospitality.ShowPointOfInterest',
      contentType: undefined,
      // @ts-ignore
      linkedComponent: Detail_POI,
    });
    //setLoaderClosed(true);
  }
  
  const handleClick = (name, value?): void => {
    sendEvent(name,value)
  }
  
  const customMenu: any[] = []
  buildCustomMenu(customMenu, handleClick)
  
  const manualConfig = useMemo<any>(() => {
    retorikConfiguration.isUsedOnBorne = true;
    const configWithMenu = {
      ...retorikConfiguration,
      baseMenu: baseMenu,
      customMenu: customMenu
    };
    
    return {
      config: configWithMenu,
      //config: defaultConfig,
      viewsConfig: viewsConfiguration,
      chatbotData: chatbotData,
      addressData: addressData,
      userData: userData,
      externalEventHandler: handleActionType,
      customVoice: customVoice,
      skipLoader: false,
      defaultMode: defaultMode,
      //externalComponents: [{ component: retorikMap, name: "mapDisplay" }],
      externalComponents: externalComponents,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    retorikConfiguration,
    viewsConfiguration,
    chatbotData,
    addressData,
    customVoice,
    defaultMode,
    retorikMap,
  ]);
  
  const currentMode = useMode()
  useEffect(() => {
    console.log("currentMode", currentMode);
    setLoaderClosed(true);  
  }, [currentMode])
  
  return (
    <React.Fragment>
    {manualConfig && addressData ? (
      <RetorikAgent {...manualConfig} />
    ) : (
      <React.Fragment>
      <Loader text="Waiting for agent data..." />
      </React.Fragment>
    )}
    
    {/* Log in step to open parameters */}
    {loggingIn && (
      <LoginWindow
      onValidationSuccessfull={handleSuccessfullValidation}
      onClose={() => setLoggingIn(false)}
      />
    )}
    
    {/* Settings button */}
    {showSettings && (
      <div className="absolute w-screen h-screen inset-0 grid grid-cols-8">
      <Settings onClose={toggleSettings} />
      </div>
    )}
    </React.Fragment>
  );
};

export default Chatbot;
